import {
	BaseMeasure,
	CalculatedMeasure,
	createAttributeHelper,
	Filter,
	FilterRelations,
	isFilterRelations,
	isMembersFilter,
	JaqlDataSourceForDto,
} from '@sisense/sdk-data';
import { filterFactory, measureFactory } from '@sisense/sdk-data';
import { NormalizedDataSourceField } from '@/services/data-sources/data-sources-service';

// Type to represent a function arg
type ParsedArg = string | number | ParsedFunctionCall;

// Type to represent the structure parsed from the JSON
type ParsedFunctionCall = {
	function: string;
	args: ParsedArg[];
};

export function getFactoryFunctionsMap(
	factoryName: string,
	factoryObject: Record<string, any>,
): Record<string, (...args: any[]) => any> {
	const functionsMap: Record<string, (...args: any[]) => any> = {};
	const prefix = factoryName !== '' ? factoryName + '.' : '';
	for (const key in factoryObject) {
		if (typeof factoryObject[key] === 'function') {
			functionsMap[`${prefix}${key}`] = factoryObject[key];
		} else if (typeof factoryObject[key] === 'object') {
			const nestedFunctionsMap = getFactoryFunctionsMap(
				'',
				factoryObject[key],
			);
			for (const nestedKey in nestedFunctionsMap) {
				functionsMap[`${prefix}${key}.${nestedKey}`] =
					nestedFunctionsMap[nestedKey];
			}
		}
	}
	return functionsMap;
}

export const measureFactoryFunctionsMap = getFactoryFunctionsMap(
	'measureFactory',
	measureFactory,
);
export const filterFactoryFunctionsMap = getFactoryFunctionsMap(
	'filterFactory',
	filterFactory,
);

function executeFunction(
	functionPath: string,
	args: any[],
): Filter | FilterRelations | BaseMeasure | CalculatedMeasure {
	const parts = functionPath.split('.');
	const factoryName = parts[0];

	if (factoryName === 'filterFactory') {
		if (filterFactoryFunctionsMap[functionPath]) {
			return (
				filterFactoryFunctionsMap[functionPath] as (
					...params: any[]
				) => Filter | FilterRelations
			)(...args);
		}
	} else if (factoryName === 'measureFactory') {
		if (measureFactoryFunctionsMap[functionPath]) {
			return (
				measureFactoryFunctionsMap[functionPath] as (
					...params: any[]
				) => BaseMeasure | CalculatedMeasure
			)(...args);
		}
	}

	throw new Error(
		`Function "${functionPath}" not found in filterFactory or measureFactory.`,
	);
}

function createAttribute(
	attributeName: string,
	dataSource: JaqlDataSourceForDto,
	dataFields: NormalizedDataSourceField[],
) {
	const dataField = dataFields.find(
		(field) => `DM.${field.table}.${field.column}` === attributeName,
	);
	if (!dataField) {
		throw new Error(
			`Data field "${attributeName}" not found in data fields.`,
		);
	}
	return createAttributeHelper({
		expression: dataField.expression,
		dataType: dataField.type,
		granularity: dataField.level,
		format: undefined,
		sort: undefined,
		dataSource,
	});
}

function processArg(
	arg: ParsedArg,
	dataSource: JaqlDataSourceForDto,
	dataFields: NormalizedDataSourceField[],
) {
	if (typeof arg === 'object' && arg !== null && arg.function && arg.args) {
		return processNode(arg, dataSource, dataFields);
	}
	if (typeof arg === 'string' && arg.startsWith('DM.')) {
		return createAttribute(arg, dataSource, dataFields);
	}
	return arg;
}

/**
 * Recursively processes a parsed JSON object to execute filterFactory and measureFactory functions.
 *
 * @param parsedObject - The parsed JSON object representing a function call or a nested structure.
 * @returns The result of the executed function or the processed nested structure.
 */
function processNode(
	parsedObject: ParsedFunctionCall,
	dataSource: JaqlDataSourceForDto,
	dataFields: NormalizedDataSourceField[],
): Filter | FilterRelations | BaseMeasure | CalculatedMeasure {
	const { function: functionPath, args } = parsedObject;
	// Recursively process arguments that are also function calls
	const processedArgs = args.map((arg) =>
		processArg(arg, dataSource, dataFields),
	);
	return executeFunction(functionPath, processedArgs);
}

function postProcessFilter(filter: Filter) {
	const { attribute, config } = filter;
	if (isMembersFilter(filter) && 'granularity' in attribute) {
		const granularity = attribute.granularity;
		// Convert values to ISO 8601 string -- e.g., 2011 to "2011-01-01"
		return filterFactory.members(
			attribute,
			filter.members.map((member) => {
				if (granularity === 'Years' && !isNaN(Number(member))) {
					return `${member}-01-01T00:00:00`;
				}
				return member;
			}),
			config,
		);
	}
	return filter;
}

function postProcessFilters(
	filters: Filter | FilterRelations,
): Filter[] | FilterRelations {
	if (isFilterRelations(filters as FilterRelations | Filter[])) {
		return filters as FilterRelations;
	}
	return [filters as Filter].map(postProcessFilter);
}

/**
 * Translate JSON string to filters
 *
 * @example
 *
 * {
 * "function": "filterFactory.logic.or",
 * "args": [
 * {
 * "function": "filterFactory.startsWith",
 *      "args": ["DM.Country.Country", "A"]
 *    },
 *    {
 *      "function": "filterFactory.measureGreaterThan",
 *      "args": [
 *        {
 *          "function": "measureFactory.sum",
 *          "args": ["DM.Commerce.Revenue", "Total Revenue"]
 *        },
 *        1000
 *      ]
 *    }
 *  ]
 * }
 *
 * is translated to the following Filter[] or FilterRelations object:
 * filterFactory.logic.or([
 * filterFactory.startsWith("DM.Country.Country", "A"),
 * filterFactory.measureGreaterThan(
 * measureFactory.sum("DM.Commerce.Revenue", "Total Revenue"),
 * 1000
 * )
 * ])
 *
 * @param filtersJson - A JSON string representing the filters
 * @returns A Filter[] or FilterRelations object
 */
export const translateFiltersFromJsonFunctionCall = (
	filtersJson: string,
	dataSource: JaqlDataSourceForDto,
	dataFields: NormalizedDataSourceField[],
): Filter[] | FilterRelations => {
	const parsedJson = JSON.parse(filtersJson);
	const result = processNode(parsedJson, dataSource, dataFields) as
		| Filter
		| FilterRelations;

	const postProcessedFilters = postProcessFilters(result);
	return postProcessedFilters;
};
